<template>
  <div class="flex flex-col gap-5">
    <div
      v-if="canGrantAllAccess"
      class="flex flex-wrap items-center justify-between gap-3"
    >
      <div>
        <div class="text-sm font-medium">Access Mode</div>
        <div class="text-sm text-tertiary">Retain staff can be given the whole platform in one go.</div>
      </div>
      <RadioTabbed
        v-model="mode"
        :options="[
          { value: 'all', label: 'All Access' },
          { value: 'specific', label: 'Specific Access' }
        ]"
      />
    </div>

    <Alert
      v-if="state.all"
      severity="info"
      icon="circle-info"
      title="All Access"
      :message="
        canGrantAllAccess
          ? 'This user can see every dealership on the platform, including any added in the future.'
          : 'This user can see every dealership on the platform. Only an admin can change this.'
      "
      :show-dismiss-button="false"
      :show-close-button="false"
    />

    <template v-else>
      <div>
        <div class="text-sm font-medium">Dealership Access</div>
        <div class="text-sm text-tertiary mb-3">
          Ticking an org grants every dealership in it, including sites added in the future. Expand a branch to pick
          individual sites instead.
        </div>
        <AccessTree
          v-model="treeSelection"
          :dealerships="dealershipPool"
          :implied-dealership-ids="manufacturerCoveredIds"
          :scopable-org-ids="scopableOrgIds"
        />
      </div>

      <div v-if="canGrantBrandAccess">
        <div class="text-sm font-medium">Brand Access</div>
        <div class="text-sm text-tertiary mb-3">
          Optional. Grants every franchised dealership of a brand, across all orgs. Multi-brand and used-car sites are
          never included.
        </div>
        <Select
          v-if="manufacturerOptions.length"
          v-model="state.manufacturers"
          mode="multiple"
          chips
          :chips-displayed="10"
          auto-expand
          :close-on-select="false"
          searchable
          :hide-selected="true"
          value-key="id"
          label-key="name"
          placeholder="None selected"
          :options="manufacturerOptions"
        />
        <div
          v-else
          class="text-sm text-tertiary"
        >
          There are no brands you can hand out.
        </div>
        <div
          v-if="state.manufacturers.length"
          class="mt-3"
        >
          <div class="text-sm text-tertiary mb-2">Limit brand access to regions (optional)</div>
          <Select
            v-model="state.manufacturerRegions"
            mode="multiple"
            chips
            :close-on-select="false"
            value-key="id"
            label-key="name"
            placeholder="All regions"
            :options="regionOptions"
          />
        </div>
      </div>

      <Alert
        v-if="resolvedDealershipCount === 0"
        severity="warning"
        icon="triangle-exclamation"
        title="No Access"
        message="The current selection doesn't give access to any dealerships."
        :show-dismiss-button="false"
        :show-close-button="false"
      />
      <Alert
        v-else
        :severity="resolvedDealershipCount > 10 ? 'warning' : 'info'"
        :icon="resolvedDealershipCount > 10 ? 'triangle-exclamation' : 'circle-info'"
        :title="`Access to ${resolvedDealershipCount} dealership${resolvedDealershipCount === 1 ? '' : 's'}`"
        :message="
          resolvedDealershipCount > 10
            ? `You are giving access to ${resolvedDealershipCount} dealerships. Please make sure this is correct.`
            : 'Based on the current selection.'
        "
        :show-dismiss-button="false"
        :show-close-button="false"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { watch, ref, computed } from 'vue'
// components
import AccessTree from '@/components/unique/access/AccessTree.vue'
import Alert from '@/components/alert/Alert.vue'
import RadioTabbed from '@/components/forms/RadioTabbed.vue'
import Select from '@/components/forms/Select.vue'
// stores
import { useAuthStore } from '@/stores/AuthStore'
import { useAccessStore } from '@/stores/AccessStore'
// utilities
import { resolveDealershipIdsFromGrants } from '@/utilities/access/accessGroupings'
// types
import type { AccessDealership } from '@/utilities/access/accessGroupings'
import type { AccessTreeSelection, ScopableOrgIds } from '@/utilities/access/accessTree'
import type { UserAccessGrant } from '@/types/domain/user-access'

/** Role ID of the Retain admin role */
const ADMIN_ROLE_ID = 1

/** user.userType of Retain's own staff */
const RETAIN_USER_TYPE = '1'

const props = withDefaults(
  defineProps<{
    /** The user's access grants */
    modelValue?: UserAccessGrant[]
  }>(),
  {
    modelValue: () => []
  }
)

const emit = defineEmits<{ 'update:modelValue': [UserAccessGrant[]] }>()

const authStore = useAuthStore()
const accessStore = useAccessStore()

/** Editor state distilled from the grants array */
const state = ref({
  all: false,
  orgs: [] as number[],
  dealerships: [] as number[],
  manufacturers: [] as number[],
  manufacturerRegions: [] as string[]
})

/** Whether the session user is Retain staff rather than a dealer user */
const isRetainUser = computed(() => authStore.details.userType == RETAIN_USER_TYPE)

/** The session user's own grants, which cap what they can hand out */
const sessionGrants = computed<UserAccessGrant[]>(() => accessStore.accessAllowed.grants ?? [])

/** Whether the session user holds unrestricted access themselves */
const sessionHasAllAccess = computed(() => sessionGrants.value.some(grant => grant.scopeType === 'all'))

/** Only Retain admins can hand out unrestricted access */
const canGrantAllAccess = computed(
  () => isRetainUser.value && authStore.roles.some(role => Number(role) === ADMIN_ROLE_ID)
)

/** Brand-wide access spans orgs, so it stays a Retain-only tool */
const canGrantBrandAccess = computed(() => isRetainUser.value)

/**
 * Orgs the session user holds outright, and so may hand out whole. Null means
 * every org, which is only true when they hold all access themselves.
 */
const scopableOrgIds = computed<ScopableOrgIds>(() => {
  // Holding everything means every org is theirs to give. With no grants to read
  // there is nothing to narrow by either, and the API rejects anything too wide
  if (sessionHasAllAccess.value || !sessionGrants.value.length) {
    return null
  }

  return sessionGrants.value
    .filter((grant): grant is UserAccessGrant & { scopeId: number } => grant.scopeType === 'org' && grant.scopeId !== null)
    .map(grant => grant.scopeId)
})

// Options are limited to the session user's own resolved access
const dealershipPool = computed(() => accessStore.accessAllowed.dealerships as AccessDealership[])
const regionOptions = computed(() => accessStore.accessAllowed.regions)

/** Brands the session user may hand out, which is all of them for all-access */
const manufacturerOptions = computed(() => {
  // Holding everything means every brand is theirs to give, as does having no
  // grants to read (the API has the final say on what's allowed)
  if (sessionHasAllAccess.value || !sessionGrants.value.length) {
    return accessStore.accessAllowed.manufacturers
  }

  const held = new Set(
    sessionGrants.value.filter(grant => grant.scopeType === 'manufacturer').map(grant => grant.scopeId)
  )

  return accessStore.accessAllowed.manufacturers.filter(manufacturer => held.has(manufacturer.id))
})

/** The access mode shown on the tabbed switch */
const mode = computed({
  get: (): string => (state.value.all ? 'all' : 'specific'),
  set: (value: string): void => {
    state.value.all = value === 'all'
  }
})

/** The org and dealership scopes, as edited by the tree */
const treeSelection = computed({
  get: (): AccessTreeSelection => ({ orgs: state.value.orgs, dealerships: state.value.dealerships }),
  set: (value: AccessTreeSelection): void => {
    state.value.orgs = value.orgs
    state.value.dealerships = value.dealerships
  }
})

/**
 * Build the manufacturer grants, one per brand or one per brand/region pair when
 * the selection is narrowed to regions.
 *
 * @returns The manufacturer grants
 */
function manufacturerGrants(): UserAccessGrant[] {
  return state.value.manufacturers.flatMap((manufacturerId): UserAccessGrant[] => {
    // No region qualifier means the grant covers all regions
    if (state.value.manufacturerRegions.length === 0) {
      return [{ scopeType: 'manufacturer', scopeId: manufacturerId, region: null }]
    }

    return state.value.manufacturerRegions.map(region => ({
      scopeType: 'manufacturer',
      scopeId: manufacturerId,
      region
    }))
  })
}

/** Dealerships already covered by a brand grant, flagged in the tree */
const manufacturerCoveredIds = computed(() =>
  resolveDealershipIdsFromGrants(manufacturerGrants(), dealershipPool.value)
)

/** Sites that sit inside an org the user is granted as a whole */
const orgScopedDealershipIds = computed(() => {
  const ids = new Set<number>()

  for (const dealership of dealershipPool.value) {
    if (typeof dealership.orgId === 'number' && state.value.orgs.includes(dealership.orgId)) {
      ids.add(dealership.id)
    }
  }

  return ids
})

/**
 * Convert the editor state into a grants array.
 *
 * @returns The grants the current selection represents
 */
function stateToGrants(): UserAccessGrant[] {
  // All access is a single grant that supersedes everything else
  if (state.value.all) {
    return [{ scopeType: 'all', scopeId: null, region: null }]
  }

  const grants: UserAccessGrant[] = state.value.orgs.map(orgId => ({
    scopeType: 'org',
    scopeId: orgId,
    region: null
  }))

  grants.push(...manufacturerGrants())

  // Sites already covered by an org scope would be redundant as their own grant
  state.value.dealerships
    .filter(dealershipId => !orgScopedDealershipIds.value.has(dealershipId))
    .forEach(dealershipId => {
      grants.push({ scopeType: 'dealership', scopeId: dealershipId, region: null })
    })

  return grants
}

/**
 * Distil a grants array into editor state.
 * Manufacturer region qualifiers are collapsed into one shared region set;
 * differing per-manufacturer regions are unioned (an accepted simplification).
 *
 * @param grants - The grants to read
 */
function grantsToState(grants: UserAccessGrant[]): void {
  const newState = {
    all: false,
    orgs: [] as number[],
    dealerships: [] as number[],
    manufacturers: [] as number[],
    manufacturerRegions: [] as string[]
  }

  for (const grant of grants ?? []) {
    // Each scope type maps onto its own part of the editor
    if (grant.scopeType === 'all') {
      newState.all = true
    } else if (grant.scopeType === 'org' && grant.scopeId !== null) {
      if (!newState.orgs.includes(grant.scopeId)) newState.orgs.push(grant.scopeId)
    } else if (grant.scopeType === 'manufacturer' && grant.scopeId !== null) {
      if (!newState.manufacturers.includes(grant.scopeId)) newState.manufacturers.push(grant.scopeId)
      if (grant.region && !newState.manufacturerRegions.includes(grant.region)) {
        newState.manufacturerRegions.push(grant.region)
      }
    } else if (grant.scopeType === 'dealership' && grant.scopeId !== null) {
      if (!newState.dealerships.includes(grant.scopeId)) newState.dealerships.push(grant.scopeId)
    }
  }

  state.value = newState
}

/** Emit the current state as a grants array */
function emitGrants(): void {
  emit('update:modelValue', stateToGrants())
}

// Client-side preview of what the grants resolve to
const resolvedDealershipCount = computed(
  () => resolveDealershipIdsFromGrants(stateToGrants(), dealershipPool.value).length
)

// Sync incoming grants into the editor state (skip echoes of our own emit to avoid loops)
watch(
  () => props.modelValue,
  newValue => {
    if (JSON.stringify(newValue ?? []) === JSON.stringify(stateToGrants())) {
      return
    }
    grantsToState(newValue)
  },
  { immediate: true }
)

// Emit whenever the state changes (covers the tree, the brand selects and clears)
watch(
  state,
  () => {
    emitGrants()
  },
  { deep: true }
)
</script>
